/**
 * hectorLinkTarget — destination d'un `<hector-link>`, en fonctions pures.
 *
 * `HectorLink.tsx` ne garde que le rendu et le clic. Ce qui décide si une
 * citation est un lien, et ce qu'il ouvre, vit ici pour se tester sans monter
 * le composant ni doubler les visualiseurs qu'il importe.
 */

import type { CitationTarget, Ordre } from '@hector/shared-types';
import type { DocumentViewerOptions } from '@/services/documentViewerService';

/** Destination fields derived from either a resolved registry target or legacy inline attrs. */
export interface LinkFields {
  type?: 'web' | 'piece' | 'jp';
  name: string;
  url?: string;
  file_path?: string;
  /** Piece document UUID — used to resolve the path when `file_path` is absent (wiki citations). */
  documentId?: string;
  /**
   * Dossier de la pièce, quand la citation le porte. Absent des citations
   * enregistrées avant que l'agent puisse lire un autre dossier que celui de la
   * conversation, et de la grammaire historique (attributs écrits par le modèle).
   */
  caseId?: string;
  ordre?: Ordre;
  id?: string;
}

/** Map a resolved registry target to the renderer's destination fields. */
export function targetToFields(target: CitationTarget): LinkFields {
  switch (target.kind) {
    case 'piece':
      return {
        type: 'piece',
        name: target.name,
        file_path: target.filePath,
        documentId: target.documentId,
        caseId: target.caseId,
      };
    case 'legifrance':
      // Légifrance opens in a new tab like any web source; URL rebuilt by the backend.
      return { type: 'web', name: target.name, url: target.url };
    case 'web':
      return { type: 'web', name: target.name, url: target.url };
    case 'jp':
      return { type: 'jp', name: target.name, ordre: target.ordre, id: target.decisionId };
  }
}

/**
 * Is the web URL a *usable* destination, or a fabricated placeholder?
 *
 * Why: the agent (legacy grammar) frequently emitted `url="https://www.legifrance.gouv.fr"`
 * (bare homepage), `url="#"` or `url=""` — clicking those strands the lawyer on a
 * useless page. Anything without a real path or query string is treated as non-usable.
 */
export function isWebUrlUsable(url?: string): boolean {
  if (!url) return false;
  const trimmed = url.trim();
  if (trimmed === '' || trimmed === '#') return false;
  try {
    const parsed = new URL(trimmed);
    const hasPath = parsed.pathname !== '' && parsed.pathname !== '/';
    const hasQuery = parsed.search.length > 1;
    return hasPath || hasQuery;
  } catch {
    // Not an absolute URL → not a usable web target.
    return false;
  }
}

/**
 * Does the file_path point at an actual file?
 *
 * Why: some sources yield a folder path (e.g. `cases/{id}/Dossier test Techinnov`)
 * or a path with no extension, which the document viewer cannot open. A real
 * document key always ends with a file extension. Whitespace in the path is fine
 * — those are genuine S3 keys.
 */
export function isPieceFilePathUsable(filePath?: string): boolean {
  if (!filePath) return false;
  const lastSegment = filePath.replace(/^\/+/, '').split('/').pop() || '';
  return /\.[a-z0-9]{2,5}$/i.test(lastSegment);
}

/** Ce que `DocumentViewerService.viewDocument` reçoit pour ouvrir une pièce citée. */
export type PieceOpening = Pick<
  DocumentViewerOptions,
  'filePath' | 'fileName' | 'fileType' | 'documentId' | 'caseId'
>;

/**
 * Décide si une pièce citée peut s'ouvrir, et avec quoi.
 *
 * Une pièce s'ouvre par son chemin de stockage, ou — quand seul son identifiant
 * est connu (citations de l'Analyse avancée) — par une recherche dans la liste
 * des pièces d'un dossier, qu'il faut donc connaître.
 *
 * WHY le dossier de la citation avant celui de l'échange : l'agent lit des
 * pièces d'autres dossiers que celui de la conversation. Le visualiseur cherche
 * la pièce dans le dossier reçu par identifiant, puis **par nom**
 * (`documentPathValidator`) : avec le dossier de l'échange, la pièce d'un autre
 * dossier ouvrait un homonyme du dossier affiché, ou rien. Le dossier de
 * l'échange reste le repli des citations qui ne portent pas le leur ; il est
 * strict (celui de ce tour, jamais emprunté à un autre), et un échange sans
 * dossier n'en fournit aucun.
 *
 * @param fields Destination de la citation.
 * @param displayName Texte de la puce, qui nomme l'onglet du visualiseur.
 * @param exchangeCaseId Dossier figé sur l'échange, s'il en a un.
 * @returns De quoi ouvrir la pièce, ou `null` si elle ne peut pas s'ouvrir.
 */
export function resolvePieceOpening(
  fields: LinkFields,
  displayName: string,
  exchangeCaseId: string | undefined,
): PieceOpening | null {
  if (fields.type !== 'piece') return null;

  const caseId = fields.caseId ?? exchangeCaseId;
  const openable = isPieceFilePathUsable(fields.file_path) || (!!fields.documentId && !!caseId);
  if (!openable) return null;

  const filePath = fields.file_path ? fields.file_path.replace(/^\/+/, '') : '';
  return {
    filePath,
    fileName: displayName || filePath.split('/').pop() || 'document',
    fileType: filePath.split('.').pop()?.toLowerCase() || '',
    // When the path is unknown (wiki citations), the viewer resolves it from
    // `documentId` + `caseId` (GET /cases/{caseId}/documents, match by id).
    documentId: fields.documentId,
    caseId,
  };
}
